import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {AuditFilterUser} from 'audit/AuditDAO';
import Avatar, {HasAvatar} from 'ui-library/avatar/Avatar';
import {AvatarShape, AvatarSize} from 'ui-library/avatar/AvatarConstants';

import AuditLogFilterDropdown from './AuditLogFilterDropdown';
import AuditLogMultiSelectFilterDropdownContent, {
  AuditLogFilterOption,
} from './AuditLogMultiSelectFilterDropdown';

interface AuditLogUserOption extends AuditLogFilterOption, HasAvatar {
  id: string;
}

export interface AuditLogUserFilterDropdownProps {
  users: Array<AuditFilterUser>;
  selectedUserIds: Array<string>;
  onChange: (selectedUserIds: Array<string>) => void;
  disabled?: boolean;
}

function userToOption(user: AuditFilterUser): AuditLogUserOption {
  return {
    id: user.id,
    name: user.name,
    handle: user.handle,
    value: user.id,
    label: user.name,
  };
}

function UserOptionContent({user}: {user: AuditLogUserOption}): JSX.Element {
  return (
    <span className={css(styles.userOptionContent)}>
      <Avatar entity={user} size={AvatarSize.XSMALL} shape={AvatarShape.CIRCLE} showTooltip={false} />
      <span className={css(styles.userLabels)}>
        <span className={css(styles.userName)}>{user.name}</span>
        <span className={css(styles.userHandle)}>@{user.handle}</span>
      </span>
    </span>
  );
}

const LABEL = 'User';

export default function AuditLogUserFilterDropdown(props: AuditLogUserFilterDropdownProps): JSX.Element {
  const {users, selectedUserIds, onChange, disabled} = props;
  const options = useMemo(() => users.map((u) => userToOption(u)), [users]);

  return (
    <AuditLogFilterDropdown
      label={LABEL}
      count={selectedUserIds.length}
      disabled={disabled}
      content={
        <AuditLogMultiSelectFilterDropdownContent<AuditLogUserOption>
          options={options}
          selectedValues={selectedUserIds}
          onChange={onChange}
          renderOptionContent={(option) => <UserOptionContent user={option} />}
        />
      }
    />
  );
}

const styles = StyleSheet.create({
  userOptionContent: {
    alignItems: 'center',
    display: 'flex',
    gap: '6px',
  },
  userLabels: {
    display: 'flex',
    flexDirection: 'column',
  },
  userName: {
    fontSize: '12px',
  },
  userHandle: {
    fontSize: '11px',
    color: Color.GRAY_500,
  },
});
